import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { trackSearch } from '../utils/analytics'
import { useLoading } from '../context/LoadingContext'
import { getBoundsRadius } from '../utils/mapUtils'

/**
 * Custom hook for common map interaction handlers
 * Handles radius changes, entity selection, and geocoding
 * Note: Filter toggle logic is now in useMapFiltersWithToggle hook
 */
export function useMapHandlers({
  userLocation,
  setUserLocation,
  setSearchLocationName,
  fetchNearbyEntities,
  geocode,
  searchLocation,
  entities,
  setEntities,
  setSelectedEntityId,
  setEntitySearchQuery,
  setShowSearchDropdown,
  mapRef,
  isImmersiveMode,
  setRadius,
  onTabChange,
  panelState,
  setIsSearchPanelMinimized,
  saveLocation
}) {
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoading()

  const handleRadiusChange = useCallback((newRadius) => {
    if (setRadius) {
      setRadius(newRadius)
    }
    if (fetchNearbyEntities) {
      // Use the map's current center, not userLocation — after a pan or fitBounds
      // the map center may differ from userLocation, so fetching from userLocation
      // would miss entities that are visible on screen.
      const center = mapRef?.current?.getCenter()
      const location = center
        ? { latitude: center.lat, longitude: center.lng }
        : userLocation
      if (location) fetchNearbyEntities(location, newRadius)
    }
  }, [mapRef, userLocation, fetchNearbyEntities, setRadius])

  const handleBoundsChange = useCallback((map) => {
    const center = map.getCenter()
    const radiusKm = getBoundsRadius(map)
    if (setRadius) setRadius(radiusKm)
    if (fetchNearbyEntities) {
      fetchNearbyEntities(
        { latitude: center.lat, longitude: center.lng },
        radiusKm
      )
    }
  }, [fetchNearbyEntities, setRadius])

  const geocodeAddress = useCallback(async () => {
    if (!geocode || !searchLocation) return

    const operationId = `geocode-${Date.now()}`
    startLoading(operationId)
    
    try {
      const result = await geocode(searchLocation)

      if (result) {
        const location = {
          latitude: result.latitude,
          longitude: result.longitude
        }
        
        // Update search location name with the formatted address
        if (setSearchLocationName) {
          setSearchLocationName(result.displayName)
        }
        
        // Update user location to center map on searched location
        if (setUserLocation) {
          setUserLocation(location)
        }
        
        // Save search location for persistence across navigation
        if (saveLocation) {
          saveLocation({
            latitude: location.latitude,
            longitude: location.longitude,
            name: result.displayName
          })
        }
        
        // Fly map to geocoded location — MapBoundsListener moveend will
        // derive both center and radius from the new bounds and fetch entities
        if (mapRef?.current) {
          mapRef.current.flyTo([result.latitude, result.longitude], mapRef.current.getZoom())
        } else if (fetchNearbyEntities) {
          // Fallback: map not yet ready, fetch directly
          await fetchNearbyEntities(location)
        }
        
        trackSearch('location', searchLocation)
      }
    } finally {
      stopLoading(operationId)
    }
  }, [searchLocation, geocode, mapRef, fetchNearbyEntities, setSearchLocationName, setUserLocation, isImmersiveMode, onTabChange, panelState, setIsSearchPanelMinimized, saveLocation, startLoading, stopLoading])

  const handleSelectEntity = useCallback((entity) => {
    // Transform snake_case API response to camelCase if needed
    const transformedEntity = {
      ...entity,
      currentLatitude: entity.currentLatitude || entity.current_latitude,
      currentLongitude: entity.currentLongitude || entity.current_longitude,
      currentAddress: entity.currentAddress || entity.current_address,
      imageUrl: entity.imageUrl || entity.image_url
    }

    // In immersive mode, navigate to the entity detail page
    if (isImmersiveMode) {
      navigate(`/entity/${transformedEntity.id}`)
      if (setEntitySearchQuery) setEntitySearchQuery('')
      if (setShowSearchDropdown) setShowSearchDropdown(false)
      return
    }

    // CRITICAL: Minimize panel when marker is clicked to give prominence to popup
    // This ensures the entity popup is fully visible and centered
    if (isImmersiveMode && onTabChange && panelState !== 'minimized') {
      onTabChange('search', 'minimized')
    } else if (!isImmersiveMode && setIsSearchPanelMinimized) {
      setIsSearchPanelMinimized(true)
    }

    if (transformedEntity.currentLatitude && transformedEntity.currentLongitude) {
      // Check if this entity is already in the entities list
      const entityExists = entities.some(e => e.id === transformedEntity.id)

      if (!entityExists && setEntities) {
        // Add this entity to the entities list so it shows up as a marker
        setEntities(prevEntities => [...prevEntities, transformedEntity])
      }

      // Mark this entity as selected so its popup can be opened
      if (setSelectedEntityId) {
        setSelectedEntityId(transformedEntity.id)
      }

      try {
        trackSearch('entity', transformedEntity.name)
      } catch (err) {
        // Analytics tracking failed silently
      }

      // Zoom to entity location with offset to account for popup visibility
      if (mapRef?.current) {
        // Pan the map so entity is visible with popup (offset left to account for panel)
        const latOffset = 0.01 // Slight northward offset to ensure popup is visible
        const lngOffset = 0.02 // Offset west to center entity when panel is minimized
        
        mapRef.current.setView(
          [transformedEntity.currentLatitude + latOffset, transformedEntity.currentLongitude - lngOffset], 
          14,
          { animate: true, duration: 0.5 }
        )
      }

      // Don't set userLocation - keep the user's actual location intact
      // The map will just pan to the entity's location
    }

    if (setEntitySearchQuery) setEntitySearchQuery('')
    if (setShowSearchDropdown) setShowSearchDropdown(false)
  }, [entities, setEntities, setSelectedEntityId, setEntitySearchQuery, setShowSearchDropdown, mapRef, isImmersiveMode, navigate])

  return {
    handleRadiusChange,
    handleBoundsChange,
    geocodeAddress,
    handleSelectEntity
  }
}
